import React, { useEffect, useRef, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import BackButton from '../../../components/Common/BackButton';
import { gradeSelectOptions, GradeType, JobRoleType } from '../../../interfaces/teacher.interface';
import { UserOnboardingStepEnum } from '../../../interfaces/user-onboarding.interface';
import { RootState } from '../../../store/store';
import { setTeacherOnboarding, setUserOnboarding } from '../../../store/userSlice';
import { saveTeacherSurvey } from '../services/Teacher.service';
import { TeacherOnboardingPagesEnum } from '../TeacherOnboarding';

const JOB_ROLE_OPTIONS = [
  { value: JobRoleType.Teacher, label: 'Teacher' },
  { value: JobRoleType.Librarian, label: 'Librarian' },
  { value: JobRoleType.Admin, label: 'Admin' },
  { value: JobRoleType.Counselor, label: 'Counselor' },
  { value: JobRoleType.Other, label: 'Other' },
];

export interface OnboardingClassInfoProps {
  onSuccess?: () => void;
  onBack?: () => void;
}

const OnboardingClassInfo: React.FC<OnboardingClassInfoProps> = ({ onSuccess, onBack }) => {
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const teacherOnboarding = useSelector((state: RootState) => state.user.teacherOnboarding);
  const [exitAnimation, setExitAnimation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [jobRole, setJobRole] = useState<JobRoleType | null>(teacherOnboarding?.jobRole || null);
  const [customJobRole, setCustomJobRole] = useState(teacherOnboarding?.jobRoleOther || '');
  const [selectedGrades, setSelectedGrades] = useState<GradeType[]>(teacherOnboarding?.grades || []);
  const [customGrade, setCustomGrade] = useState(teacherOnboarding?.customGrade || '');
  const [studentCount, setStudentCount] = useState(teacherOnboarding?.studentCount?.toString() || '');
  const [mobileNumber, setMobileNumber] = useState(teacherOnboarding?.mobileNumber || '');

  const [isJobRoleDropdownOpen, setIsJobRoleDropdownOpen] = useState(false);
  const [isGradeDropdownOpen, setIsGradeDropdownOpen] = useState(false);
  const jobRoleDropdownRef = useRef<HTMLDivElement>(null);
  const gradeDropdownRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setExitAnimation(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (jobRoleDropdownRef.current && !jobRoleDropdownRef.current.contains(event.target as Node)) {
        setIsJobRoleDropdownOpen(false);
      }
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(event.target as Node)) {
        setIsGradeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleGradeSelection = (grade: GradeType) => {
    setSelectedGrades((prev) => (prev?.includes(grade) ? prev.filter((g) => g !== grade) : [...(prev || []), grade]));
    if (grade !== GradeType.Other) setCustomGrade('');
  };

  const handleBack = () => {
    setExitAnimation(true);
    setTimeout(() => {
      if (onBack) onBack();
      else navigateTo(TeacherOnboardingPagesEnum.OnboardingSchoolInfo);
    }, 250);
  };

  const handleContinue = async () => {
    if (!studentCount.trim() || !jobRole || !selectedGrades?.length || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const data = {
      jobRole,
      jobRoleOther: jobRole === JobRoleType.Other ? customJobRole.trim() : undefined,
      grades: selectedGrades,
      customGrade: selectedGrades.includes(GradeType.Other) ? customGrade.trim() : undefined,
      studentCount: parseInt(studentCount),
      mobileNumber: mobileNumber.trim() || undefined,
    };
    dispatch(setTeacherOnboarding({ data }));
    try {
      await saveTeacherSurvey({ ...teacherOnboarding, ...data }, () => {
        dispatch(
          setUserOnboarding({
            data: {
              ...teacherOnboarding,
              ...data,
              currentStep: UserOnboardingStepEnum.HowDidYouFindUs,
            },
          }),
        );

        setExitAnimation(true);
        setTimeout(() => {
          if (onSuccess) onSuccess();
          else navigateTo(TeacherOnboardingPagesEnum.OnboardingHowDidYouFindUs);
        }, 250);
      });
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const handleStudentCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    if (/^\d*$/.test(value)) {
      setStudentCount(value);
    }
  };

  const handleMobileNumberChange = (value: string) => {
    // Allow numbers, spaces, dashes, parentheses, and plus sign
    if (/^[\d\s\-() +]*$/.test(value)) {
      setMobileNumber(value);
    }
  };

  const isFormValid = studentCount.trim() && jobRole && selectedGrades?.length > 0;

  const selectedJobRoleLabel = JOB_ROLE_OPTIONS.find((o) => o.value === jobRole)?.label;

  return (
    <div
      className={`max-w-full mx-auto px-4 py-6 sm:py-8 transition-all duration-500 ${exitAnimation ? 'opacity-0 transform translate-y-10' : 'opacity-100'}`}
    >
      <div className="space-y-6 text-left">
        {/* Role & Grades - 2 columns */}
        <div className="grid grid-cols-1 gap-x-6 gap-y-6">
          {/* What is your role? */}
          <div className="relative" ref={jobRoleDropdownRef}>
            <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
              What is your role? <span className="text-storyseconderror">*</span>
            </h2>
            <div
              className="w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg bg-white cursor-pointer flex items-center justify-between"
              onClick={() => setIsJobRoleDropdownOpen(!isJobRoleDropdownOpen)}
            >
              <span className={selectedJobRoleLabel ? 'text-storyblackText' : 'opacity-50'}>
                {selectedJobRoleLabel || 'Select'}
              </span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            {isJobRoleDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-secondaryStoryLightGrayInputBorder rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {JOB_ROLE_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${jobRole === opt.value ? 'bg-gray-50' : ''}`}
                    onClick={() => {
                      setJobRole(opt.value);
                      setIsJobRoleDropdownOpen(false);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {jobRole === JobRoleType.Other && (
            <input
              type="text"
              value={customJobRole}
              onChange={(e) => setCustomJobRole(e.target.value)}
              placeholder="Please specify your role"
              className="w-full mt-2 p-3 text-center border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
              aria-label="Custom job role"
            />
          )}

          {/* Which grades do you serve? */}
          <div className="relative" ref={gradeDropdownRef}>
            <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
              Which grades do you serve? <span className="text-storyseconderror">*</span>
            </h2>
            <div
              className="w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg bg-white cursor-pointer flex items-center justify-between"
              onClick={() => setIsGradeDropdownOpen(!isGradeDropdownOpen)}
            >
              <div className="truncate flex flex-wrap gap-1">
                {selectedGrades.length > 0 ? (
                  selectedGrades.map((grade) => {
                    const option = gradeSelectOptions.find((opt) => opt.value === grade);
                    return (
                      <span
                        key={grade}
                        className="px-2 py-0.5 rounded text-xs sm:text-sm"
                        style={{
                          backgroundColor: option?.color?.replace('rgb', 'rgba').replace(')', ', 0.2)') || '',
                          color: option?.color || '',
                        }}
                      >
                        {option?.title}
                      </span>
                    );
                  })
                ) : (
                  <span className="opacity-50">Select grades</span>
                )}
              </div>
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            {isGradeDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-secondaryStoryLightGrayInputBorder rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {gradeSelectOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between ${selectedGrades.includes(option.value as GradeType) ? 'bg-gray-50' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGradeSelection(option.value as GradeType);
                    }}
                  >
                    <span>{option.title}</span>
                    {selectedGrades.includes(option.value as GradeType) && (
                      <svg className="w-4 h-4 text-storyblueSecondLightText" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            )}
            {selectedGrades.includes(GradeType.Other) && (
              <input
                type="text"
                value={customGrade}
                onChange={(e) => setCustomGrade(e.target.value)}
                placeholder="Please specify the grade"
                className="w-full mt-2 p-3 text-center border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
                aria-label="Custom grade"
              />
            )}
          </div>
        </div>

        {/* Student Count */}
        <div className="grid grid-cols-1 gap-x-6 gap-y-6">
          <div className="relative">
            <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
              How many students? <span className="text-storyseconderror">*</span>
            </h2>
            <input
              type="text"
              value={studentCount}
              onChange={handleStudentCountChange}
              placeholder="45"
              className="w-full p-3 text-center border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
              maxLength={3}
            />
          </div>
        </div>

        {/* Mobile Number (optional) */}
        <div className="relative">
          <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
            Mobile Number <span className="font-normal text-secondaryStoryGrayButtonText">(optional)</span>
          </h2>
          <PhoneInput
            country={'us'}
            value={mobileNumber}
            onChange={(phoneNumber) => handleMobileNumberChange(phoneNumber)}
            placeholder="Mobile Number"
            inputClass="!w-full !px-4 !pl-12 !py-2 !border !border-secondaryStoryGrayBorder !rounded-lg !text-base !text-center !text-secondaryStoryBlackText"
            containerClass="!w-full"
            buttonClass="!border !border-secondaryStoryGrayBorder !rounded-l-lg !bg-white hover:!bg-gray-50"
            dropdownClass="!border !border-secondaryStoryGrayBorder !rounded-lg !shadow-lg"
            // disableCountryCode={false}
            countryCodeEditable={true}
            disableCountryGuess={false}
            preferredCountries={['us', 'ca', 'gb', 'au']}
            disableInitialCountryGuess={true}
            inputStyle={{
              width: '100%',
              height: '42px',
              fontSize: '16px',
              paddingLeft: '48px',
              border: '1px solid',
              borderRadius: '8px',
              outline: 'none',
            }}
            containerStyle={{
              width: '100%',
            }}
            buttonStyle={{
              border: '1px solid',
              borderRadius: '8px 0 0 8px',
              backgroundColor: 'white',
            }}
          />
          <p className="mt-2 text-xs sm:text-[10px] text-secondaryStoryDescriptionGreyTextColor">
            For class updates. Use +countrycode and number (e.g. +14155552671). Build My Story service texts (reminders,
            login help). Recurring; frequency varies. Msg&data rates may apply. Reply{' '}
            <span className="font-bold">STOP</span> to cancel, <span className="font-bold">HELP</span> for help.{' '}
            <Link
              to="https://buildmystory.com/terms-and-conditions/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondaryStoryAdditionalLightBlueText hover:underline"
            >
              Terms
            </Link>{' '}
            •{' '}
            <Link
              to="https://buildmystory.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondaryStoryAdditionalLightBlueText hover:underline"
            >
              Privacy
            </Link>
          </p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-8">
        <div>
          <BackButton onClick={handleBack}></BackButton>
        </div>

        <button
          className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm sm:text-base w-auto justify-center ${
            isFormValid && !isSubmitting ? '' : 'opacity-50 cursor-not-allowed'
          }`}
          onClick={handleContinue}
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default OnboardingClassInfo;
